import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  User, 
  signInWithPopup, 
  signInWithRedirect,
  getRedirectResult,
  setPersistence,
  indexedDBLocalPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail
} from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, googleProvider, db } from '../firebase';
import { CustomBranding } from '../types';

interface AuthContextType {
  user: User | null;
  userData: UserData | null;
  loading: boolean;
  isAdmin: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (email: string, password: string, displayName: string) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshUserData: () => Promise<void>;
}

interface UserData {
  uid: string;
  email: string;
  displayName: string;
  photoURL?: string;
  youtubeToken?: string;
  isAdmin?: boolean;
  blocked?: boolean;
  blockedReason?: string;
  customBranding?: CustomBranding;
  partyPass?: {
    active: boolean;
    expiresAt: Date;
    purchasedAt: Date;
  };
  proSubscription?: {
    active: boolean;
    expiresAt: Date;
    purchasedAt: Date;
  };
  createdAt: Date;
  lastLoginAt: Date;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    
    // Initialize auth in the correct order
    (async () => {
      // 1. Configure auth persistence with fallbacks for Safari/iOS
      try {
        await setPersistence(auth, indexedDBLocalPersistence);
      } catch (e1) {
        try {
          await setPersistence(auth, browserLocalPersistence);
        } catch (e2) {
          try {
            await setPersistence(auth, browserSessionPersistence);
          } catch (e3) {
            console.error('Failed to set any persistence:', e3);
          }
        }
      }

      // 2. Check for redirect result BEFORE setting up listener
      // This ensures we capture the auth state from the redirect
      let expectingRedirect = false;
      try {
        expectingRedirect = sessionStorage.getItem('qroke-redirect') === '1';
      } catch {}

      // Always check for redirect result on page load (especially on mobile)
      try {
        console.log('Checking for redirect result...');
        const redirectResult = await getRedirectResult(auth);
        
        if (redirectResult && redirectResult.user) {
          console.log('Redirect sign-in successful:', redirectResult.user.email);
          const u = redirectResult.user;
          const userRef = doc(db, 'users', u.uid);
          const userDoc = await getDoc(userRef);
          const data = {
            email: u.email,
            displayName: u.displayName,
            photoURL: u.photoURL,
            lastLoginAt: new Date()
          };
          if (!userDoc.exists()) {
            await setDoc(userRef, { ...data, createdAt: new Date() });
          } else {
            await setDoc(userRef, data, { merge: true });
          }
        } else if (expectingRedirect) {
          console.log('No redirect result found despite expecting one');
        }
      } catch (e) {
        console.error('Redirect sign-in error:', e);
      } finally {
        try { sessionStorage.removeItem('qroke-redirect'); } catch {}
      }

      // 3. NOW set up the auth state listener
      try {
        unsubscribe = onAuthStateChanged(auth, async (user) => {
          console.log('Auth state changed:', user ? user.email : 'signed out');
          try {
            setUser(user);
            if (user) {
              await loadUserData(user.uid);
            } else {
              setUserData(null);
            }
          } catch (error) {
            console.error('Error in auth state change:', error);
          } finally {
            setLoading(false);
          }
        });
      } catch (error) {
        console.error('Error setting up auth listener:', error);
        setLoading(false);
      }
    })();

    return () => {
      if (unsubscribe) {
        try {
          unsubscribe();
        } catch (error) {
          // Unsubscribe error
        }
      }
    };
  }, []);

  const loadUserData = async (uid: string) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        
        // If user is blocked, force sign out immediately
        if (data.blocked === true) {
          await firebaseSignOut(auth);
          setUser(null);
          setUserData(null);
          alert('Your account has been suspended. Contact support if you believe this is an error.');
          return;
        }
        
        setUserData({
          ...data,
          uid,
          createdAt: data.createdAt?.toDate(),
          lastLoginAt: data.lastLoginAt?.toDate(),
          partyPass: data.partyPass ? {
            ...data.partyPass,
            expiresAt: data.partyPass.expiresAt?.toDate(),
            purchasedAt: data.partyPass.purchasedAt?.toDate()
          } : undefined,
          proSubscription: data.proSubscription ? {
            ...data.proSubscription,
            expiresAt: data.proSubscription.expiresAt?.toDate(),
            purchasedAt: data.proSubscription.purchasedAt?.toDate()
          } : undefined
        } as UserData);
      }
    } catch (error) {
      // Error loading user data
    }
  };

  const signInWithGoogle = async () => {
    // Always try popup first — works on all modern browsers (including mobile)
    // when triggered by a direct user gesture (tap/click).
    // Only fall back to redirect if popup is explicitly blocked.
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      // Save user data to Firestore
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      const userData = {
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        lastLoginAt: new Date()
      };
      if (!userDoc.exists()) {
        await setDoc(userRef, { ...userData, createdAt: new Date() });
      } else {
        await setDoc(userRef, userData, { merge: true });
      }
    } catch (error) {
      const code = (error as any)?.code || '';
      // User closed the popup — not an error, just bail out
      if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
        return;
      }
      // Popup blocked or not supported — fall back to redirect
      const popupBlocked = code.includes('popup') || code === 'auth/operation-not-supported-in-this-environment';
      if (popupBlocked) {
        try { sessionStorage.setItem('qroke-redirect', '1'); } catch {}
        await signInWithRedirect(auth, googleProvider);
        return;
      }
      throw error as any;
    }
  };

  const signInWithEmail = async (email: string, password: string) => {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      const user = result.user;
      
      // Update user data in Firestore
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      const userData = {
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        lastLoginAt: new Date()
      };
      
      if (!userDoc.exists()) {
        await setDoc(userRef, { ...userData, createdAt: new Date() });
      } else {
        await setDoc(userRef, userData, { merge: true });
      }
    } catch (error) {
      throw error as any;
    }
  };

  const signUpWithEmail = async (email: string, password: string, displayName: string) => {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      const user = result.user;
      
      // Create user document in Firestore
      const userRef = doc(db, 'users', user.uid);
      await setDoc(userRef, {
        email: user.email,
        displayName: displayName,
        photoURL: user.photoURL,
        createdAt: new Date(),
        lastLoginAt: new Date()
      });
    } catch (error) {
      throw error as any;
    }
  };

  const resetPassword = async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      throw error as any;
    }
  };

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
      setUserData(null);
    } catch (error) {
      throw error;
    }
  };

  const refreshUserData = async () => {
    if (user) {
      await loadUserData(user.uid);
    }
  };

  // Check if user is admin (your email)
  const isAdmin = user?.email === 'bkentner@gmail.com' || userData?.isAdmin === true;

  const value = {
    user,
    userData,
    loading,
    isAdmin,
    signInWithGoogle,
    signInWithEmail,
    signUpWithEmail,
    resetPassword,
    signOut,
    refreshUserData
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};